import classNames from 'classnames';
import debounce from 'debounce-promise';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactTable, { Column, SortingRule } from 'react-table-6';
import { HasClassName } from '../../component-utils';
import { useSelector } from '../../hook-utils';
import { Extension, Identifier, Region } from '../../model';
import { actions } from '../../store';
import { selectFavExtensionFolderId, selectIsExpertise, selectMyAccess, selectMyself, selectRegions } from '../../store/reducers/config';
import { getSelectedExtension, selectExtension, selectRegionExtenions } from '../../store/reducers/queueAgents';
import { TableRow } from '../../table-utils';
import ExtensionMenu from '../AgentMenu';
import { PresenceInfo } from '../AgentsTable';
import { ExtensionInfo, RenderSelection } from '../ExtensionsPage';
import { MenuTarget } from '../Menu';
import MoreButton from '../MoreButton';
import './ExtensionPage.scss';

interface MenuState {
  target: MenuTarget
  item: Extension
}

interface Props extends HasClassName {
  regionId: string
  filter: string
}

export default function MobileExtensionsTable({ className, regionId, filter }: Props) {
  const [selected, setSelected] = useState<Identifier>()
  const [menuState, setMenuState] = useState<MenuState>()
  const dispatch = useDispatch()
  const extensions = useSelector(s => selectRegionExtenions(s, regionId))
  const folderId = useSelector(selectFavExtensionFolderId)
  const data = useMemo(() => extensions ? extensions : [], [extensions])
  const regions = useSelector(selectRegions)
  const myId = useSelector(selectMyself)
  const myself = useSelector(s => myId ? selectExtension(s, myId) : undefined)
  const isExpertise = useSelector(selectIsExpertise)
  const open = useSelector(getSelectedExtension)
  const access = useSelector(selectMyAccess)

  const [sorted, setSorted] = useState<SortingRule[]>([{ id: 'extension', desc: false }])

  const [curFilter, setCurFilter] = useState(filter)
  const updateFilter = useMemo(() => debounce(setCurFilter, 1000), [])

  const onMore = useCallback((e: React.SyntheticEvent, item: Extension, target: MenuTarget) => {
    e.preventDefault()
    e.stopPropagation()
    setMenuState({ target, item })
  }, [])

  useEffect(() => {
    updateFilter(filter)
  }, [filter, updateFilter])

  useEffect((() => {
    if (!extensions && folderId) {
      dispatch(actions.fetchExtensionsRequeust(regionId, folderId))
    } else {
      return
    }
  }), [dispatch, regionId, extensions, folderId])


  if (!regions || !myself || !myId || !access) return null

  const f = curFilter.toLowerCase()

  const columns = getColumns(onMore, regions).filter(c => isExpertise ? true : c.id !== 'expertise')
  const region = f !== '' ? regions.filter(r => r.name.toLowerCase().indexOf(f) > -1) : undefined
  return <div className='ExtensionTableBody'>
    <ReactTable className={classNames('ExtensionTable', className, 'MobileExtensionTable')}
      data={f !== '' ? data.filter(c => {
        return c.id.toLowerCase().indexOf(f) > -1 ||
          c.name.toLowerCase().indexOf(f) > -1 ||
          c.department.toLowerCase().indexOf(f) > -1 ||
          c.expertise.toLowerCase().indexOf(f) > -1 ||
          c.server.toLowerCase().indexOf(f) > -1 || (region && region.find(r => r.id === c.regionId) !== undefined)
      }) : data}
      columns={columns}
      showPagination={true}
      defaultPageSize={20}
      minRows={0}
      loading={!extensions}
      sorted={sorted}
      NoDataComponent={() => null}
      getTrProps={(_: any, row?: TableRow<Extension>) => ({
        className: row && ((selected && row.original.id === selected.id && row.original.server === selected.server) || (menuState && row.original.id === menuState.item.id && row.original.server === menuState.item.server)) ? 'selected' : undefined,
        onClick() {
          if (!row) { return }
          setSelected(row.original)
        },
      })}
      onSortedChange={(newSort, column) => {
        setSorted(newSort)
      }}
    />

    {menuState && <ExtensionMenu {...menuState}
      extension={menuState.item}
      onClose={() => setMenuState(undefined)} />}

    {open && ((!open.edit && (myId.id === open.id.id || access.features.clientViewExtensionDetails)) || (open.edit && ((myId.id === open.id.id && access.features.configMyExtension) || access.features.configAllExtensions))) && <RenderSelection id={open.id} edit={open.edit} myId={myId} />}

  </div >
}

function getColumns(onMore: (e: React.MouseEvent, item: Extension, target: MenuTarget) => void, regions: Region[]): Array<Column<Extension>> {

  const cols: Column<Extension>[] = [{
    id: 'presence',
    accessor: a => a.presence,
    className: 'presence',
    Cell: ({ original: a }: TableRow<Extension>) => <PresenceInfo extension={a} />,
    width: 32,
    resizable: false,
  }, {
    id: 'extension',
    Header: 'Extension',
    accessor: a => a.id,
    width: 70,
  }, {
    id: 'details',
    Header: 'Details',
    accessor: a => a.name,
    Cell: (row: TableRow<Extension>) => <div className='agent'>
      <ExtensionInfo extension={row.original} />
    </div>,
  }, {
    id: 'actions',
    accessor: q => <MoreButton className='menu-button' onClick={e => onMore(e, q, e.currentTarget)} />,
    width: 35,
    sortable: false,
    resizable: false,
  },
  ]

  return cols
}
